import * as React from "react";
import { useEffect, useState } from "react";
import { ImageField, ImageInput, ImageInputProps, useTranslate } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import CropIcon from "@mui/icons-material/Crop";
import ImageCropDialog from "./ImageCropDialog";
import { CROP_SLOTS, CropSlotName } from "./cropSlots";
import { fetchAsObjectUrl, isCroppedFileName } from "./cropImage";
import { getImageSrc } from "../previewKit";

type ImageCropInputProps = Omit<ImageInputProps, "children" | "options"> & {
  source: string;
  slot: CropSlotName;
};

interface CropOrigin {
  url: string;
  name: string;
}

// Module-level so it survives the form unmounting while the preview tab is open.
const origins = new WeakMap<File, CropOrigin>();

interface DialogState {
  origin: CropOrigin | null;
  name: string;
  fresh: boolean;
  fetched: boolean;
  loading: boolean;
  error: string | null;
}

const useNaturalSize = (src: string | null) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    setSize(null);
    if (!src) return;
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setSize({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return size;
};

const ImageCropInput = ({ slot, source, ...props }: ImageCropInputProps) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const value = useWatch({ name: source });
  const item = Array.isArray(value) ? value[0] : value;
  const rawFile: File | undefined = item?.rawFile;
  const src = getImageSrc(value);
  const size = useNaturalSize(src);
  const cropSlot = CROP_SLOTS[slot];

  const [dialog, setDialog] = useState<DialogState | null>(null);

  const openDialog = (state: Partial<DialogState> & { name: string }) =>
    setDialog({
      origin: null,
      fresh: false,
      fetched: false,
      loading: false,
      error: null,
      ...state,
    });

  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const origin = { url: URL.createObjectURL(file), name: file.name };
    origins.set(file, origin);
    openDialog({ origin, name: file.name, fresh: true });
  };

  const handleCropClick = async () => {
    const known = rawFile && origins.get(rawFile);
    if (known) {
      openDialog({ origin: known, name: known.name });
      return;
    }
    if (!src) return;
    if (rawFile) {
      openDialog({ origin: { url: src, name: rawFile.name }, name: rawFile.name });
      return;
    }

    const name = decodeURIComponent(src.split("/").pop() || "image");
    openDialog({ name, loading: true });
    try {
      const url = await fetchAsObjectUrl(src);
      setDialog(
        (current) =>
          current && { ...current, origin: { url, name }, fetched: true, loading: false }
      );
    } catch (error: any) {
      setDialog(
        (current) =>
          current && { ...current, loading: false, error: error?.message || String(error) }
      );
    }
  };

  const handleCancel = () => {
    if (dialog?.fetched && dialog.origin) URL.revokeObjectURL(dialog.origin.url);
    setDialog(null);
  };

  const handleApply = (file: File) => {
    if (dialog?.origin) origins.set(file, dialog.origin);
    setValue(
      source,
      { rawFile: file, src: URL.createObjectURL(file), title: file.name },
      { shouldDirty: true, shouldTouch: true }
    );
    setDialog(null);
  };

  const cropped = !!rawFile && isCroppedFileName(rawFile.name);
  const defaultPreset = cropSlot.presets[0];

  return (
    <Box sx={{ width: "100%" }}>
      <ImageInput source={source} options={{ onDrop: handleDrop }} {...props}>
        <ImageField source="src" />
      </ImageInput>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<CropIcon />}
          onClick={handleCropClick}
          disabled={!src}
        >
          {translate("imageCrop.open")}
        </Button>
        {size && (
          <Typography variant="caption" color="text.secondary">
            {translate("imageCrop.currentSize", size)}
          </Typography>
        )}
        {cropped && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={translate("imageCrop.cropped")}
          />
        )}
        {rawFile && !cropped && (
          <Typography variant="caption" color="warning.main">
            {translate("imageCrop.serverResizeHint", {
              width: defaultPreset.width,
              height: defaultPreset.height,
            })}
          </Typography>
        )}
      </Stack>
      <ImageCropDialog
        open={!!dialog}
        imageUrl={dialog?.origin?.url ?? null}
        loading={dialog?.loading}
        loadError={dialog?.error}
        sourceName={dialog?.name ?? ""}
        slot={cropSlot}
        initialSize={dialog?.fresh ? undefined : size ?? undefined}
        cancelLabelKey={dialog?.fresh ? "imageCrop.keepOriginal" : "ra.action.cancel"}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default ImageCropInput;
